"use client"
import { IconClose } from "@/app/(site)/_components/Atoms/Icons"
import Modal from "@/app/(site)/_components/Atoms/Modal"
import GCTextElement from "@/app/(site)/_components/GCMolecules/GCTextElement"
import ProductAttributeTag from "@/app/(site)/_components/Page/Product/ProductDetail/ProductAttributeMapper/ProductAttributeTag"
import { useCustomScrollbar } from "@/app/(site)/_utils/_hooks/useCustomScrollBar"
import { useHideGladlyIcon } from "@/app/(site)/_utils/_hooks/useHideGladlyIcon"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { type ProductAttribute } from "@/lib/content-service/types/ContentServiceProduct"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { HIT_TARGET_CLASS } from "@/src/utils/a11y/hitTarget"
import { clsx } from "@/src/utils/clsx"

interface ProductAttributeModalProps {
    attributes: ProductAttribute[]
    title: string
    langCode?: string
}

export default function ProductAttributeModal({
    attributes,
    langCode,
    title
}: ProductAttributeModalProps) {
    const { closeProductAttributeModal, isProductAttributeModalOpen } =
        useEcommerceStore()

    useHideGladlyIcon(isProductAttributeModalOpen)
    const { containerRef, thumbHeight, thumbTop, isScrollable } = useCustomScrollbar({
        isActive: isProductAttributeModalOpen,
        dependencies: [attributes]
    })

    if (!attributes) return null
    return (
        <Modal
            open={isProductAttributeModalOpen}
            onOpenChange={closeProductAttributeModal}
        >
            <Modal.Content
                aria-describedby="product-attribute-modal"
                className="flex max-h-[330px] flex-col pb-12 lg:max-h-[668px]"
            >
                <Modal.Description className="sr-only">{title} Modal</Modal.Description>
                <Modal.Title className="mb-24 flex items-center justify-between">
                    <GCTextElement
                        as="span"
                        className="Trim_Mono_POC_Text_15_130_-5_500_uppercase"
                        text={title}
                    />
                    <Modal.Close
                        className={clsx("p-5", FOCUS_VISIBLE_CLASS, HIT_TARGET_CLASS)}
                    >
                        <IconClose className="h-10 w-10" />
                    </Modal.Close>
                </Modal.Title>
                <div className="relative flex min-h-0 flex-1 flex-col">
                    <div
                        ref={containerRef}
                        className="flex min-h-0 flex-1 flex-col gap-40 overflow-y-auto scrollbar-hide"
                    >
                        {attributes.map((attr: ProductAttribute, index: number) => (
                            <div
                                key={`${attr.label}-${index}`}
                                className="flex flex-col gap-y-12"
                            >
                                <ProductAttributeTag label={attr.label} />
                                <GCTextElement
                                    as="p"
                                    className="Trade_Gothic_Text_14_150_0_300"
                                    text={attr.information || ""}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                {/* Custom scrollbar positioned relative to modal content */}
                {isScrollable && (
                    <div className="pointer-events-none absolute bottom-16 right-4 top-16 w-1">
                        {/* Scrollbar track */}
                        <div className="absolute inset-0 bg-col-bg-subtle" />
                        {/* Scrollbar thumb */}
                        <div
                            className="absolute w-full bg-col-bg-secondary"
                            style={{
                                top: `${thumbTop}%`,
                                height: `${thumbHeight}%`,
                                minHeight: "20px"
                            }}
                        />
                    </div>
                )}
            </Modal.Content>
        </Modal>
    )
}
